import { FC, useEffect, useState } from "react"

import { ReactComponent as IconClose } from "@common_assets/svg/cross.svg"
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

const SHOW_DELAY_MS = 5000

interface IExportPendingBannerProps {
  visible: boolean
}

const ExportPendingBanner: FC<IExportPendingBannerProps> = ({ visible }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [dismissed, setDismissed] = useState(false)
  const [showAfterDelay, setShowAfterDelay] = useState(false)

  useEffect(() => {
    if (!visible) {
      setShowAfterDelay(false)
      setDismissed(false)
      return
    }

    const timer = setTimeout(() => {
      setShowAfterDelay(true)
    }, SHOW_DELAY_MS)

    return () => clearTimeout(timer)
  }, [visible])

  if (!visible || !showAfterDelay || dismissed) return null

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        top: 0,
        // 130px = half the sidebar width, centers banner within the content area
        left: "calc(50% + 130px)",
        transform: "translateX(-50%)",
        [theme.breakpoints.down("desktop")]: {
          left: "50%",
        },
        zIndex: theme.zIndex.snackbar,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        py: 1.25,
        px: 2,
        boxShadow: theme.shadows[6],
        maxWidth: 420,
        width: "auto",
      }}
    >
      <IconButton
        onClick={() => setDismissed(true)}
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          padding: 0.5,
          color: theme.palette.text.secondary,
        }}
      >
        <IconClose style={{ width: 18, height: 18 }} />
      </IconButton>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pr: 2 }}>
        <CircularProgress size={18} thickness={4} />
        <Stack direction="column" spacing={0.25} sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: theme.typography.fontWeightMedium }}
          >
            {t("export.yourReportIsBeingGenerated")}
          </Typography>
          <Typography
            variant="body3"
            sx={{ color: theme.palette.text.secondary }}
          >
            {t("export.largeReportsMayTakeSeveralMinutes")}
          </Typography>
        </Stack>
      </Stack>
    </Box>,
    document.body,
  )
}

export default ExportPendingBanner
